/**
 * Push do PWA via OneSignal (SDK v16).
 *
 * O QUE ESTE ARQUIVO EXISTE PARA EVITAR
 * -------------------------------------
 * Push falha em silêncio de umas cinco maneiras diferentes, e todas terminam
 * igual: a pessoa marca o toggle, vê "ativado", e nunca recebe nada. Num
 * produto de alerta isso é pior que não ter o recurso. Então aqui a regra é
 * nunca reportar sucesso sem que o navegador tenha de fato concedido.
 *
 * As armadilhas, na ordem em que mordem:
 *
 *  1. iOS em aba. Safari no iPhone só entrega push para site INSTALADO na
 *     tela de início. Em aba, `window.Notification` nem existe. Detectamos e
 *     explicamos, em vez de deixar o toggle fingir que funcionou.
 *  2. Prompt sem gesto. iOS exige que o pedido de permissão saia de um
 *     clique. Por isso o `autoPrompt: false` no init e o pedido só dentro de
 *     `ativarPush()`, que só roda a partir do botão.
 *  3. Escopo do service worker. O escopo raiz é do vite-plugin-pwa; o do
 *     OneSignal vai em /push/onesignal/. Ver o comentário no próprio
 *     OneSignalSDKWorker.js.
 *  4. CSP. cdn.onesignal.com e api.onesignal.com precisam estar liberados no
 *     Caddyfile, senão o SDK nem carrega.
 *  5. Permissão negada antes. Uma vez negado, o navegador não pergunta de
 *     novo — só nas configurações do site. `requestPermission()` devolve
 *     "denied" na hora, e a tela precisa dizer isso.
 */

/** App ID é público (vai no bundle). A chave de API é do backend, nunca daqui. */
const APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID ?? "";

const SDK_URL = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";

/** Precisa casar com o caminho real em public/ e com o escopo do SW. */
const SW_PATH = "push/onesignal/OneSignalSDKWorker.js";
const SW_SCOPE = "/push/onesignal/";

interface OneSignalSDK {
  init(opts: Record<string, unknown>): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  Notifications: {
    permission: boolean;
    requestPermission(): Promise<void>;
  };
  User: {
    PushSubscription: {
      optedIn?: boolean;
      id?: string;
      optIn(): Promise<void>;
      optOut(): Promise<void>;
    };
  };
}

declare global {
  interface Window {
    OneSignalDeferred?: Array<(os: OneSignalSDK) => void | Promise<void>>;
    OneSignal?: OneSignalSDK;
  }
}

export type MotivoIndisponivel =
  | "sem-app-id"
  | "ios-em-aba"
  | "sem-suporte";

export interface Disponibilidade {
  ok: boolean;
  motivo?: MotivoIndisponivel;
}

function ehIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ se apresenta como Macintosh; o maxTouchPoints desempata.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}

export function instaladoNaTelaDeInicio(): boolean {
  const iosStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia("(display-mode: standalone)").matches;
}

/**
 * Push é possível NESTE aparelho, neste momento?
 *
 * Capacidade primeiro, plataforma depois: a ausência de `Notification` e de
 * `serviceWorker` é o teste que não mente. O ramo de iOS existe só para dar
 * uma instrução acionável em vez de "seu navegador não suporta".
 */
export function disponibilidade(): Disponibilidade {
  if (!APP_ID) return { ok: false, motivo: "sem-app-id" };
  const temAPI = "Notification" in window && "serviceWorker" in navigator;
  if (!temAPI) {
    return { ok: false, motivo: ehIOS() ? "ios-em-aba" : "sem-suporte" };
  }
  // iOS com as APIs presentes mas fora da tela de início ainda não entrega.
  if (ehIOS() && !instaladoNaTelaDeInicio()) {
    return { ok: false, motivo: "ios-em-aba" };
  }
  return { ok: true };
}

let carregando: Promise<OneSignalSDK> | null = null;

function carregarSDK(): Promise<OneSignalSDK> {
  if (window.OneSignal) return Promise.resolve(window.OneSignal);
  if (carregando) return carregando;

  carregando = new Promise<OneSignalSDK>((resolve, reject) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.defer = true;
    // O erro mais comum aqui é CSP bloqueando o cdn — e sem este reject a
    // promessa ficaria pendurada para sempre e o botão giraria sem fim.
    script.onerror = () =>
      reject(new Error("não consegui carregar o SDK do OneSignal (CSP?)"));
    document.head.appendChild(script);
    window.OneSignalDeferred.push((os) => resolve(os));

    // onerror cobre rede e CSP, mas não cobre o script carregar e o SDK
    // nunca esvaziar a fila de deferidos. Sem este teto a promessa fica
    // pendurada e o botão gira para sempre — que foi o sintoma em 11/09/26.
    setTimeout(
      () => reject(new Error("SDK do OneSignal não respondeu em 15s")),
      15_000,
    );
  });
  return carregando;
}

function _espera(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

let iniciado = false;

/**
 * Carrega, inicializa e amarra o external_id ao usuário logado.
 *
 * O external_id é o `users.id` — é assim que o backend endereça o push sem
 * manter tabela de aparelhos. Idempotente: chamar de novo só refaz o login.
 *
 * ATENÇÃO — `serviceWorkerPath` e `serviceWorkerParam` abaixo só valem se a
 * customização de service worker estiver LIGADA no painel do OneSignal
 * (Web Push settings). Com ela desligada o SDK ignora os dois e usa o que o
 * painel manda, que por padrão é `/OneSignalSDKWorker.js` no escopo `/` —
 * caminho que não existe aqui e que, se existisse, brigaria com o service
 * worker do PWA.
 *
 * Isso não está na documentação do init(); descobrimos em 11/09/26 lendo o
 * JSON de configuração que o SDK baixa do painel, onde aparece
 * `"customizationEnabled": false`. O sintoma era o init() rejeitar com
 * "Timeout", porque o registro do worker falhava em silêncio.
 *
 * Valores que o painel precisa ter, casando com os daqui:
 *   path               /push/onesignal/
 *   workerName         OneSignalSDKWorker.js
 *   registrationScope  /push/onesignal/
 */
export async function prepararPush(userId: string): Promise<void> {
  const os = await carregarSDK();
  if (!iniciado) {
    await os.init({
      appId: APP_ID,
      serviceWorkerPath: SW_PATH,
      serviceWorkerParam: { scope: SW_SCOPE },
      autoResubscribe: true,
      // Nada de prompt automático: iOS exige gesto, e prompt na primeira
      // visita é a forma mais rápida de virar "bloqueado para sempre".
      promptOptions: { slidedown: { prompts: [{ type: "push", autoPrompt: false }] } },
    });
    iniciado = true;
  }
  await os.login(userId);
}

/**
 * Confere se o OneSignal registrou o worker no escopo que a gente pediu.
 *
 * Só faz sentido depois do init. Serve para um caso específico e traiçoeiro:
 * o painel com a customização desligada faz o SDK ignorar o nosso caminho e
 * tentar a raiz. Quando isso acontece o init costuma estourar em timeout,
 * mas se um dia falhar de outro jeito — ou pior, se der certo e tomar o
 * escopo do PWA — isto avisa em vez de deixar quieto.
 */
export async function escopoRegistradoConfere(): Promise<boolean | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const nosso = regs.find((r) => r.scope.endsWith(SW_SCOPE));
    if (!nosso) {
      console.warn(
        "[push] nenhum service worker no escopo", SW_SCOPE,
        "— a customização de service worker está ligada no painel do OneSignal?",
      );
      return false;
    }
    return true;
  } catch {
    return null;
  }
}

export function permissaoAtual(): NotificationPermission {
  if (!("Notification" in window)) return "denied";
  return Notification.permission;
}

export function inscrito(): boolean {
  return Boolean(window.OneSignal?.User?.PushSubscription?.optedIn);
}

export type ResultadoAtivacao = "ativado" | "negado" | "erro" | "nao-pronto";

/**
 * Pede permissão e inscreve. **Só de dentro de um clique**, e só depois de
 * `prepararPush()` ter terminado.
 *
 * A separação entre preparar e ativar não é organização — é requisito do
 * iOS. O Safari exige que o pedido de permissão saia enquanto a ativação
 * transiente do gesto ainda vale, e ela não sobrevive a um await de rede.
 *
 * A primeira versão desta função fazia `await prepararPush()` aqui dentro:
 * baixava o SDK, inicializava e só então pedia permissão. No desktop
 * funcionava; no iPhone o diálogo nunca aparecia e a promessa ficava
 * pendurada — botão em "Ativando…" para sempre. Por isso `prepararPush` foi
 * para a montagem da tela e aqui não existe nenhum await antes do
 * requestPermission.
 *
 * Devolve "negado" sem lançar quando o usuário recusa: recusa é resposta, não
 * falha, e a tela precisa distinguir as duas para saber o que dizer.
 */
export async function ativarPush(): Promise<ResultadoAtivacao> {
  const os = window.OneSignal;
  if (!os || !iniciado) return "nao-pronto";

  try {
    if (permissaoAtual() === "default") {
      // Nenhum await acima desta linha. De propósito.
      await Promise.race([
        os.Notifications.requestPermission(),
        _espera(20_000),
      ]);
    }
    // A fonte da verdade é o navegador, não o retorno da chamada: se o
    // diálogo foi dispensado sem escolha, a promessa pode nem settlar.
    if (permissaoAtual() !== "granted") return "negado";

    await Promise.race([os.User.PushSubscription.optIn(), _espera(20_000)]);
    return "ativado";
  } catch (e) {
    console.error("[push] falha ao ativar", e);
    return "erro";
  }
}

export async function desativarPush(): Promise<void> {
  try {
    await window.OneSignal?.User?.PushSubscription?.optOut();
  } catch (e) {
    // Não propaga: o que manda é a preferência no backend, e ela já foi
    // desligada. Ficar inscrito no OneSignal sem opt-in no banco só gasta
    // uma chamada de API que não encontra destinatário.
    console.warn("[push] optOut falhou", e);
  }
}
